import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTranslations,
  createTranslation,
  updateTranslation,
  deleteTranslation,
  exportTranslations,
  importTranslations,
} from "../services/configurations/translations";
import { Label } from "../interfaces";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "DOWNLOAD" | "IMPORT";

const handleTranslationActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createTranslation,
        fnMsg: t("Translations.translationsApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateTranslation,
        fnMsg: t("Translations.translationsApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteTranslation,
        fnMsg: t("Translations.translationsApiDeleteLabel"),
      };
    case "DOWNLOAD":
      return {
        actionFn: exportTranslations,
        fnMsg: t("Translations.translationsApiDownloadLabel"),
      };
    case "IMPORT":
      return {
        actionFn: importTranslations,
        fnMsg: t("Translations.translationsApiImportLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useTranslations = (type: UseType, translationId?: number, widget?: string) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";
  const navigate = useNavigate();

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const translationAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleTranslationActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then(async (response) => {
        const errorImportLabels = (action === "IMPORT") && await response?.result?.filter((label:any) => label?.status === "ERROR");
        if (errorImportLabels?.length > 0) {
          setAppState((prev)=>({
            ...prev,
            errorLabelsList: errorImportLabels,
            labelsImportProcessed: response?.result?.length ?? 0,
          }));
          sessionStorage.removeItem('import-filters-table');
          navigate('/translations/import/errorimport');
        }
        else {
          toast.current.replace({
            severity: "success",
            summary: "",
            detail: `${fnMsg}`,
            life: 3000,
          });
          return "SUCCESS";
        };
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getTranslations(appid, widget))
          .then((response) => {
            const labels: Label[] = response?.data;
            const translations = labels.map((label) => {
              const enTranslation = label.translations.find(
                (translation) => translation.languageisocode === "en"
              );
              const esTranslation = label.translations.find(
                (translation) => translation.languageisocode === "es"
              );
              return {
                id: label.id || 0,
                key: label.key,
                description: label.description,
                translationenglishid: enTranslation?.id || 0,
                envalue: enTranslation?.value,
                translationspanishid: esTranslation?.id || 0,
                esvalue: esTranslation?.value,
                type: label.type,
                widgets: label.widgets,
                isCustom: !!label.createdby,
                releaseversion: label?.releaseversion,
                createdat: label?.createdat ?? null,
                updatedat: label?.updatedat ?? null,
              };
            });
            setData(translations);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type, translationId]);

  return {
    data,
    translationAction,
    reloadFn,
  };
};
